<template>
  <div
    v-if="options.length > 0"
    ref="variantPickerRef"
    class="product-variant-picker"
  >
    <div
      v-if="$textDictionary['pdp.configuration.title.label']"
      class="product-variant-picker__title"
    >
      <span>
        {{ $textDictionary['pdp.configuration.title.label'] }}
      </span>
    </div>
    <div class="product-variant-picker__items">
      <div
        v-for="optionGroup in options"
        :key="optionGroup.code!"
        class="product-variant-picker__item"
      >
        <div class="product-variant-picker__option-groups">
          <ProductVariantPickerOptionGroup v-bind="optionGroup" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductVariantPickerOptionGroup } from '@hypercodestudio/basler-components';
import type { ProductVariantPickerOptionProps as VariantOptionProps } from '@hypercodestudio/basler-components';

import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { isDefined } from '~/utils/guards/isDefined';
import { usePdpPrefetchOnInteraction } from '~/composables/usePrefetchPdp';
import {
  buildVariantOptionSelectionFromAttributes,
  findVariantForOptionSelection
} from '~/utils/shop/isVariantOptionSelectable';
import {
  getLightColorVariantIconId,
  getLightColorVariantSortIndex
} from '~/utils/shop/getLightColorVariantIconId';
import {
  getSensorMonocolorVariantIconId,
  getSensorMonocolorVariantSortIndex
} from '~/utils/shop/getSensorMonocolorVariantIconId';

const store = useProductDetailPageStore();
const { $textDictionary, $activeLocale } = useNuxtApp();
const variantPickerRef = ref<HTMLElement | null>(null);
usePdpPrefetchOnInteraction(variantPickerRef);

const optionCodes = computed(() =>
  store.options.map((option) => option.attribute_code).filter(isDefined)
);

const currentSelection = computed(() =>
  buildVariantOptionSelectionFromAttributes(
    store.currentVariant?.attributes,
    optionCodes.value
  )
);

const options = computed(() =>
  store.options
    .filter(
      (optionFragment) =>
        optionFragment.values?.length && optionFragment.values.length > 1
    )
    .map((optionFragment) => {
      const optionCode = optionFragment.attribute_code ?? '';

      return {
        code: optionFragment.attribute_code,
        title:
          $textDictionary.value[
            'pdp.configuration.option-group.label'
          ]?.replace('{attribute}', optionFragment.label ?? '') ??
          optionFragment.label ??
          '',
        options:
          optionFragment.values
            ?.filter(isDefined)
            .map<VariantOptionProps & { price: number }>((value) => {
              const optionUid = value.uid ?? '';
              const matchingVariant = findVariantForOptionSelection(
                optionCode,
                optionUid,
                currentSelection.value,
                store.parentProduct?.variants,
                optionCodes.value
              );
              const urlKey = matchingVariant?.product?.url_key;
              const isSelectable = Boolean(matchingVariant && urlKey);
              const price =
                matchingVariant?.product?.price_range.minimum_price.final_price
                  .value ?? 0;

              const iconId =
                getSensorMonocolorVariantIconId(
                  optionCode,
                  value.label ?? ''
                ) ?? getLightColorVariantIconId(optionCode, value.label ?? '');

              const option = {
                link:
                  isSelectable && urlKey
                    ? {
                        uri: buildPdpUrl($activeLocale.value.webapp, urlKey)
                      }
                    : undefined,
                id: iconId ?? optionUid,
                label: value.label ?? '',
                value: optionUid,
                price,
                showColor: iconId != null,
                isActive:
                  optionUid.length > 0 &&
                  store.currentVariantOptionUids.includes(optionUid),
                isDisabled: store.loading || !isSelectable
              };

              return option;
            })
            .sort((a, b) => {
              const sortIndexA =
                getSensorMonocolorVariantSortIndex(optionCode, a.label) ??
                getLightColorVariantSortIndex(optionCode, a.label);
              const sortIndexB =
                getSensorMonocolorVariantSortIndex(optionCode, b.label) ??
                getLightColorVariantSortIndex(optionCode, b.label);

              if (sortIndexA != null && sortIndexB != null) {
                return sortIndexA - sortIndexB;
              }

              if (a.price === 0) {
                return 1;
              }
              if (b.price === 0) {
                return -1;
              }

              if (a.price !== b.price) {
                return a.price - b.price;
              }

              return a.label.localeCompare(b.label);
            })
            .map((option) => {
              const { price, ...optionWithoutPrice } = option;
              return optionWithoutPrice;
            }) ?? []
      };
    })
);
</script>

<style>
/* basler-components styles --circle-color but not --circle-mono (falls back to flat gray) */
.product-variant-picker .product-variant-option__label--circle-mono::before {
  background: linear-gradient(135deg, #2b2b2b 0%, #e8e8e8 100%);
}

.product-variant-picker .product-variant-option__label--circle-white::before,
.product-variant-picker .product-variant-option__label--circle-blue::before,
.product-variant-picker .product-variant-option__label--circle-red::before,
.product-variant-picker .product-variant-option__label--circle-infrared::before,
.product-variant-picker .product-variant-option__label--circle-swir::before,
.product-variant-picker .product-variant-option__label--circle-uv::before,
.product-variant-picker .product-variant-option__label--circle-green::before,
.product-variant-picker .product-variant-option__label--circle-unknown::before {
  background-color: transparent;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.product-variant-picker .product-variant-option__label--circle-white::before {
  background-image: url('/images/variant-swatch/light-color-white.svg');
}

.product-variant-picker .product-variant-option__label--circle-blue::before {
  background-image: url('/images/variant-swatch/light-color-blue.svg');
}

.product-variant-picker .product-variant-option__label--circle-red::before {
  background-image: url('/images/variant-swatch/light-color-red.svg');
}

.product-variant-picker
  .product-variant-option__label--circle-infrared::before {
  background-image: url('/images/variant-swatch/light-color-infrared.svg');
}

.product-variant-picker .product-variant-option__label--circle-swir::before {
  background-image: url('/images/variant-swatch/light-color-swir.svg');
}

.product-variant-picker .product-variant-option__label--circle-uv::before {
  background-image: url('/images/variant-swatch/light-color-uv.svg');
}

.product-variant-picker .product-variant-option__label--circle-green::before {
  background-image: url('/images/variant-swatch/light-color-green.svg');
}

.product-variant-picker .product-variant-option__label--circle-unknown::before {
  background-image: url('/images/variant-swatch/light-color-unknown.svg');
}
</style>
